import { FormikValues } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { Card } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';

import AppSnackbar from '../../../common/AppSnackbar';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useFindUser, usePatchUser } from '../../../user/User.hooks';
import { User } from '../../../user/User.interface';

export interface UserInfoCardProps {
  userId: number;
  onChange?: (user: User) => void;
}

const getInitialValues = (user?: User) => {
  return {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || ''
  };
};

const UserInfoCard: React.FC<UserInfoCardProps> = ({ userId, onChange }) => {
  const onChangeRef = useRef(onChange); // prevent circular refresh
  const formikRef = useRef<FormikValues>();
  const {
    user,
    isLoading: isLoadingInitialValues,
    error: findError
  } = useFindUser(userId, true);
  const { isLoading: isUpdating, error: updateError, patch } = usePatchUser(
    userId
  );

  const [initialValues, setInitialValues] = useState<Partial<User>>();

  // set the initial state of the form once loaded
  useEffect(() => {
    if (!isLoadingInitialValues && user) {
      setInitialValues(getInitialValues(user));
    }
  }, [isLoadingInitialValues, user]);

  // notify parent when a record is updated
  useEffect(() => {
    if (user && onChangeRef && onChangeRef.current) {
      onChangeRef.current(user);
    }
  }, [user]);
  return (
    <Card>
      <CardTitle>
        <PersonIcon />
        User Info
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={initialValues}
          validationSchema={Yup.object({
            firstName: Yup.string().required('Required'),
            lastName: Yup.string().required('Required')
          })}
          isSubmitting={isUpdating}
          error={findError}
          onSubmit={(user: Partial<User>) => {
            console.log('onSubmit', user);
            patch({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email
            });
          }}
        >
          <InlineForm>
            <InlineTextField
              disabled
              name='id'
              label='Employee ID'
              value={user ? `USER-${user.id}` : ''}
            />
            <InlineTextField
              name='firstName'
              label='First Name'
              placeholder="What is the employee's first name?"
              grid={{ xs: 12, md: 6 }}
            />
            <InlineTextField
              name='lastName'
              label='Last Name'
              placeholder="What is the employee's last name?"
              grid={{ xs: 12, md: 6 }}
            />
            <InlineTextField
              name='email'
              label='Email Address'
              placeholder="What is the employee's email address?"
            />
          </InlineForm>
        </InlineFormik>
        <AppSnackbar severity='error' open={Boolean(updateError)}>
          There was an error saving.
        </AppSnackbar>
      </CardContent>
    </Card>
  );
};

export default UserInfoCard;
